import { useEffect, useRef, useState } from 'react'

import {
  useInfiniteQuery,
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useToast } from '@zet/common/components/toast'
import { MY_TEAMS_ADVISOR } from '@zet/constants'
import { APP_NAME } from '@zet/end-points'
import { useUserContext } from '@zet/feature-user/api/user/hooks'
import { isApp } from '@zet/utils'
import { FIVE_MINUTES } from '@zet/utils/cacheTime'

import { useExperiments } from '../../../experiments/src/hooks'
import { i18n } from '../../../localization'
import {
  geBanners,
  getAgentDashboardForBrands,
  getBestBrands,
  getBrandDataByPartnerId,
  getBrandsCategory,
  getCBMProductDetails,
  getChoiceConnectLeadId,
  getCreditBuilderBenefits,
  getCreditScoreReferralProgram,
  getCriteriaIndexes,
  getCustomerByPartnerId,
  getExtraPayoutPercentage,
  getFilteredRecommendationCategorySpecific,
  getFilteredRecommendationPartnerSpecific,
  getGlobalShareBrandURL,
  getLoyaltyGifts,
  getLoyaltyTierDetails,
  getMagnetBenefits,
  getMyTeamsBrandPayoutDetails,
  getNextStateForProductFlow,
  getPartnerIdsV2,
  getPincodeCheck,
  getRecommendation,
  getShareBrandURLV4,
  getShareCreative,
  getTrainingForBrands,
  getZetStore,
} from './functions'
import {
  GetCriterialIndexesRequest,
  IBrandShareRequestParams,
  IRecommendationFilterPayload,
} from './types'

const GENERATE_SHARE_LINK_V4_TIMEOUT = 60

const useCheckPincode = () => {
  return useMutation(getPincodeCheck, {
    onSuccess: ({ data }) => data,
    onError: (error) => console.warn('error', error),
  })
}

function useChoiceConnectLeadId(leadId?: string) {
  return useQuery(
    ['choiceConnectLeadId', leadId],
    () => getChoiceConnectLeadId(leadId!),
    {
      enabled: !!leadId,
      retry: 1,
      retryDelay: 500,
    },
  )
}

function useCategories({ enabled = true }: { enabled?: boolean }) {
  const { data } = useUserContext()
  return useQuery({
    queryKey: ['brandsCategories', data?.id],
    queryFn: getBrandsCategory,
    staleTime: FIVE_MINUTES,
    refetchOnWindowFocus: false,
    enabled: Boolean(enabled),
  })
}

const useCheckPincodeV2 = (pincode: string, categoryId: number) => {
  return useQuery(
    ['checkPincode', pincode, categoryId],
    () => getPincodeCheck({ pincode, categoryId }),
    { enabled: categoryId ? true : false, staleTime: 0 },
  )
}

function useBrandDetailsById(partnerId: string | number) {
  const locale = i18n.locale
  return useQuery({
    queryKey: ['brandDetailsById', partnerId?.toString(), locale],
    queryFn: () => getBrandDataByPartnerId(partnerId, locale),
    enabled: !!partnerId,
    refetchOnWindowFocus: false,
    staleTime: FIVE_MINUTES,
    cacheTime: FIVE_MINUTES,
  })
}

type mutation = UseMutationResult<
  {
    link: string
    shouldPoll?: boolean | undefined
    pollDurationInMilliSeconds?: number | undefined
  },
  unknown,
  IBrandShareRequestParams,
  unknown
>

export const usePoll = (mutation: mutation) => {
  const toast = useToast()
  const [isPollInProgress, setIsPollInProgress] = useState(false)

  const getShareLinkTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )
  const startTime = useRef<number | null>(null)
  const propsRef = useRef([])

  const reset = () => {
    setIsPollInProgress(false)
    startTime.current = null
  }

  const pollShareLinkWithReset = (...props: any[]) => {
    reset()
    mutation.data = undefined
    mutation.reset()
    pollShareLink(...props)
  }

  const pollShareLink = (...props: any[]) => {
    if (props) {
      propsRef.current = props
    }
    if (!startTime.current) {
      startTime.current = new Date().getTime()
    }
    const currentTime = new Date().getTime()
    const timeDiff = (currentTime - startTime.current) / 1000
    if (
      mutation?.data?.shouldPoll === false ||
      timeDiff > GENERATE_SHARE_LINK_V4_TIMEOUT
    ) {
      if (timeDiff > GENERATE_SHARE_LINK_V4_TIMEOUT) {
        toast?.show({
          intent: 'error',
          heading: i18n.t('toastMessages.error'),
          description: i18n.t('toastMessages.somethingWentWrong'),
          hideAfter: 3000,
        })
      }
      reset()
      if (getShareLinkTimeoutRef.current) {
        clearTimeout(getShareLinkTimeoutRef.current)
      }
    } else {
      setIsPollInProgress(true)
      mutation.mutate(...propsRef.current)
    }
  }

  useEffect(() => {
    if (mutation.isSuccess) {
      if (
        mutation.data?.shouldPoll &&
        mutation.data?.pollDurationInMilliSeconds
      ) {
        getShareLinkTimeoutRef.current = setTimeout(() => {
          pollShareLink(mutation?.variables)
        }, mutation.data?.pollDurationInMilliSeconds)
      } else {
        reset()
      }
    }
    return () => {
      if (getShareLinkTimeoutRef.current) {
        clearTimeout(getShareLinkTimeoutRef.current)
      }
    }
  }, [mutation.data, mutation.isSuccess])

  useEffect(() => {
    if (mutation.isError) {
      toast?.show({
        intent: 'error',
        heading: i18n.t('toastMessages.error'),
        description: i18n.t('toastMessages.somethingWentWrong'),
        hideAfter: 3000,
      })
      reset()
    }
  }, [mutation.isError])

  return { isPollInProgress, pollShareLink, pollShareLinkWithReset }
}

export const useShareLink = () => {
  const { data: experiments } = useExperiments()

  // if (experiments?.UI_IsGenerateShareLinkV4) {
  return getShareBrandURLV4
  // } else {
  //   return getShareBrandURL
  // }
}

export const useGenerateSharableLink = () => {
  const queryClient = useQueryClient()
  const shareLink = useShareLink()

  const mutation = useMutation(shareLink, {
    onSuccess: (data) => {
      if (!data?.shouldPoll) {
        queryClient.invalidateQueries({ queryKey: ['home-widgets'] })
        queryClient.invalidateQueries(['userLeadCount'])
        queryClient.invalidateQueries(['getLeadCount'])
        queryClient.invalidateQueries(['leadFilterFields'])
        queryClient.invalidateQueries(['agentDashboardForBrands'])
      }
    },
    onError: (err) => err,
  })

  const { pollShareLinkWithReset, isPollInProgress } = usePoll(mutation)
  return {
    ...mutation,
    mutate: pollShareLinkWithReset,
    isLoading: isPollInProgress,
  }
}

const usePartnerIdsForBrand = (categoryId: number) => {
  return useQuery(
    ['PartnerIdsForBrand', categoryId],
    () => getPartnerIdsV2(categoryId),
    {
      enabled: !!categoryId,
      staleTime: FIVE_MINUTES,
    },
  )
}

function usePartnerIdsByCategoryId(id: number) {
  const queryClient = useQueryClient()
  const partnerIds = queryClient?.getQueryData([`brandsPartnerIds-${id}`])

  return partnerIds
}

const useAgentDashboardForBrands = (brandList: number) => {
  return useQuery(['agentDashboardForBrands', brandList], () =>
    getAgentDashboardForBrands(brandList),
  )
}

const useGenerateGlobalSharableLink = () => {
  return useMutation(getGlobalShareBrandURL, {
    onSuccess: (data) => {
      return data
    },
    onError: (err) => err,
  })
}

const useShareCreative = (partnerId: number, language: string) => {
  return useQuery(['brandShareCreative', partnerId, language], () =>
    getShareCreative(partnerId, language),
  )
}

const useRecommendation = (
  customerId: number,
  partnerId?: number,
  isDemoSale?: boolean,
) => {
  return useQuery(
    [`recommendation`, customerId, partnerId],
    () => getRecommendation(customerId, partnerId, isDemoSale),
    {
      enabled: customerId ? true : false,
      cacheTime: 0,
      staleTime: 0,
      retry: 5,
      retryDelay: 3 * 1000,
    },
  )
}

function useLeads(partnerId: number, searchText: string, limit: number) {
  return useInfiniteQuery(
    ['CustomerByPartnerId', partnerId, searchText],
    () => {
      return getCustomerByPartnerId(partnerId, searchText, limit)
    },
    {
      getNextPageParam: (lastPage, pages) => {
        if (pages?.length * 10 <= lastPage?.count) {
          return lastPage?.nextOffset
        }
        return undefined
      },
    },
  )
}

const useCustomerByPartnerId = (partnerId: number, searchText: string) => {
  return useInfiniteQuery(
    ['CustomerByPartnerId', { partnerId, searchText }],
    ({ pageParam = 0 }) => {
      return getCustomerByPartnerId(partnerId, searchText, pageParam)
    },
    {
      getNextPageParam: (lastPage, pages) => {
        if (lastPage?.hasNextOffset || pages?.length * 10 <= lastPage?.count) {
          return lastPage?.nextOffset
        }
        return undefined
      },
    },
  )
}

const useFilteredRecommendationForPartner = ({
  customerId,
  partnerId,
}: IRecommendationFilterPayload) => {
  return useQuery(
    ['recommendationPartnerSpecific', customerId, partnerId],
    () => getFilteredRecommendationPartnerSpecific({ customerId, partnerId }),
    { enabled: !!customerId && !!partnerId },
  )
}

const useFilteredCategoryList = ({
  customerId,
  categoryId,
  type,
  isInEligiblePartners,
}: IRecommendationFilterPayload) => {
  return useQuery(
    ['recommendation', customerId, categoryId, type],
    () =>
      getFilteredRecommendationCategorySpecific({
        customerId,
        categoryId,
        type,
      }),
    { enabled: isInEligiblePartners },
  )
}

const useBrandsTraining = (partnerId: number) => {
  return useQuery(['brandsTraining', partnerId], () =>
    getTrainingForBrands(partnerId),
  )
}

function useBestBrands() {
  return useQuery(['userBrands'], getBestBrands)
}

function useGetMagnetBenefits() {
  return useQuery(['magnetBenefits'], getMagnetBenefits)
}

function useCreditBuilderBenefits() {
  return useQuery(['creditBuilderBenefits'], getCreditBuilderBenefits)
}

function useGetExtraPayoutPercentage(id: string | number) {
  return useQuery(
    ['extraPayoutPercentage', id],
    () => getExtraPayoutPercentage(id),
    { enabled: !!id && isApp },
  )
}
function useGetLoyaltyTierDetails() {
  return useQuery(['loyaltyTierDetails'], getLoyaltyTierDetails, {
    enabled: isApp,
  })
}

const useMyTeamsBrandPayout = (brandId: number) => {
  return useQuery(
    ['myTeamsBrandPayout', brandId],
    () => getMyTeamsBrandPayoutDetails(brandId),
    { enabled: APP_NAME === MY_TEAMS_ADVISOR },
  )
}

function useGetCriteriaIndexes(suppliedData: GetCriterialIndexesRequest) {
  const locale = i18n.locale
  return useQuery(
    ['getCriteriaIndexes', locale, suppliedData],
    () => getCriteriaIndexes(suppliedData),
    { refetchOnWindowFocus: false },
  )
}
function useGetNextStateForProductFlow() {
  return useMutation(getNextStateForProductFlow)
}
function useProductFlowRecommendations() {
  return useMutation(getFilteredRecommendationPartnerSpecific)
}

function useGetBanners(screenName: string) {
  return useQuery(['getBanners', screenName], () => geBanners(screenName), {
    enabled: APP_NAME === 'zet-app',
  })
}

function useGetLoyaltyGifts() {
  return useQuery(['loyaltyGifts'], getLoyaltyGifts)
}

function useProductCBM() {
  return useQuery(['getCBMProductDetails'], () => getCBMProductDetails())
}
function useGetZetStoreDpanda({ partnerId }: { partnerId: number }) {
  return useQuery(['zetStoreDpanda'], () => getZetStore({ partnerId }))
}

function useGetCreditScoreReferralProgram(partnerId: string) {
  return useQuery(['creditScoreReferralProgram', partnerId], () =>
    getCreditScoreReferralProgram(partnerId),
  )
}

export {
  useAgentDashboardForBrands,
  useBestBrands,
  useBrandDetailsById,
  useBrandsTraining,
  useCategories,
  useCheckPincode,
  useCheckPincodeV2,
  useChoiceConnectLeadId,
  useCreditBuilderBenefits,
  useCustomerByPartnerId,
  useFilteredCategoryList,
  useFilteredRecommendationForPartner,
  useGenerateGlobalSharableLink,
  useGetBanners,
  useGetCreditScoreReferralProgram,
  useGetCriteriaIndexes,
  useGetExtraPayoutPercentage,
  useGetLoyaltyGifts,
  useGetLoyaltyTierDetails,
  useGetMagnetBenefits,
  useGetNextStateForProductFlow,
  useGetZetStoreDpanda,
  useLeads,
  useMyTeamsBrandPayout,
  usePartnerIdsByCategoryId,
  usePartnerIdsForBrand,
  useProductCBM,
  useProductFlowRecommendations,
  useRecommendation,
  useShareCreative,
}
